import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [status, setStatus] = useState('idle'); // idle | loading | success | not_found | error
  const [message, setMessage] = useState('');

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;

    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/emails/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();

      if (data.status === 'success') {
        setStatus('success');
        setMessage('You have been successfully unsubscribed from all SpendLess emails.');
      } else {
        setStatus('not_found');
        setMessage('We couldn\'t find that email in our list. You may already be unsubscribed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something went wrong. Please try again or contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <SimpleHeader />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-lg mx-auto">
          <Card className="bg-white border-none shadow-lg p-10" data-testid="unsubscribe-card">
            {status === 'success' ? (
              <div className="text-center" data-testid="unsubscribe-success">
                <div className="w-16 h-16 bg-[#6B7A5C] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-[#6B7A5C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-[#2C1810] mb-3">Unsubscribed</h1>
                <p className="text-[#5A5046] mb-6">{message}</p>
                <p className="text-[#5A5046] text-sm mb-6">
                  You will still receive order confirmations for any purchases you make.
                </p>
                <Link to="/">
                  <Button className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-3 rounded-lg">
                    Back to Home
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-[#2C1810] mb-3 text-center">Unsubscribe</h1>
                <p className="text-[#5A5046] text-center mb-8">
                  Enter your email to unsubscribe from SpendLess marketing emails.
                </p>

                <form onSubmit={handleUnsubscribe} className="space-y-4">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border-[#E5DFD0]"
                    disabled={status === 'loading'}
                    data-testid="unsubscribe-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-[#C17855] hover:bg-[#B06745] text-white py-3 rounded-lg"
                    data-testid="unsubscribe-submit-btn"
                  >
                    {status === 'loading' ? 'Processing...' : 'Unsubscribe'}
                  </Button>
                </form>

                {(status === 'not_found' || status === 'error') && (
                  <div className="mt-4 p-3 bg-[#FFF3E0] border border-[#E5DFD0] rounded-lg text-center" data-testid="unsubscribe-error">
                    <p className="text-[#5A5046] text-sm">{message}</p>
                  </div>
                )}

                <p className="text-[#A89884] text-xs text-center mt-6">
                  Note: Order confirmation emails for purchases are transactional and will still be sent.
                </p>
              </>
            )}
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UnsubscribePage;
